import React, { useEffect, useMemo, useState } from "react";
import { CalendarClock, CheckCircle2, Loader2, Upload, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { bookAppointment, uploadFile } from "../../lib/api";
import axios from "axios";
import { API } from "../../lib/api";

const dayLabel = (iso) => new Date(iso).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
const timeLabel = (iso) => new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

const groupByDay = (slots) => {
  const map = {};
  for (const s of slots || []) {
    const key = s.start_time.slice(0, 10);
    (map[key] = map[key] || []).push(s);
  }
  return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
};

const SlotGrid = ({ slots, selected, onSelect }) => (
  <div className="space-y-4 max-h-72 overflow-y-auto pr-1">
    {groupByDay(slots).map(([day, daySlots]) => (
      <div key={day}>
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{dayLabel(day + "T00:00:00")}</p>
        <div className="flex flex-wrap gap-2">
          {daySlots.map((s) => {
            const active = selected?.start_time === s.start_time;
            return (
              <button key={s.start_time} type="button" onClick={() => onSelect(s)}
                data-testid={`appointment-slot-${s.start_time}`}
                className={`text-sm px-3 py-2 border transition-colors duration-150 ${active ? "bg-brand-gold border-brand-gold text-white" : "bg-white border-border text-brand-black hover:border-brand-gold"}`}>
                {timeLabel(s.start_time)}
              </button>
            );
          })}
        </div>
      </div>
    ))}
  </div>
);

export const AppointmentBooking = ({ enquiryId }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null); // {enabled, returning, slots|options}
  const [selected, setSelected] = useState(null); // {start_time, calendar_id}
  const [booking, setBooking] = useState(false);
  const [booked, setBooked] = useState(null);
  // returning-client extras
  const [update, setUpdate] = useState("");
  const [urgent, setUrgent] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!enquiryId) return;
    axios.get(`${API}/enquiries/${enquiryId}/appointment-options`)
      .then((r) => setData(r.data))
      .catch(() => setData({ enabled: false }))
      .finally(() => setLoading(false));
  }, [enquiryId]);

  const onUpload = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setUploading(true);
    try {
      for (const f of list) {
        const res = await uploadFile(f);
        setFiles((prev) => [...prev, { id: res.id, filename: res.filename }]);
      }
    } catch (err) {
      toast.error("A file could not be uploaded.");
    } finally {
      setUploading(false);
    }
  };

  const confirm = async () => {
    if (!selected) return;
    if (data?.returning && !update.trim()) {
      toast.error("Please give us a brief update on your situation.");
      return;
    }
    setBooking(true);
    try {
      const payload = { start_time: selected.start_time, end_time: selected.end_time, calendar_id: selected.calendar_id };
      if (data?.returning) {
        payload.update = update;
        payload.urgent = urgent;
        payload.file_ids = files.map((f) => f.id);
      }
      const res = await bookAppointment(enquiryId, payload);
      setBooked({ ...selected, type: res.type });
      toast.success("Your appointment has been booked.");
    } catch (e) {
      toast.error("We could not book that time. Please choose another slot.");
    } finally {
      setBooking(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 text-sm text-brand-darkgrey py-6" data-testid="appointment-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading available appointments…
      </div>
    );
  }

  const noFree = !data?.returning && (!data?.enabled || (data?.slots || []).length === 0);
  const noPaid = data?.returning && !(data?.options || []).some((o) => (o.slots || []).length > 0);
  if (booked) {
    return (
      <div className="text-left border border-brand-gold bg-brand-grey/40 p-5" data-testid="appointment-confirmed">
        <p className="text-sm font-semibold text-brand-black flex items-center gap-2 mb-1">
          <CheckCircle2 className="h-5 w-5 text-brand-gold" strokeWidth={1.5} /> Appointment booked{booked.type ? ` — ${booked.type}` : ""}
        </p>
        <p className="text-sm text-brand-darkgrey">{dayLabel(booked.start_time)} at {timeLabel(booked.start_time)}. A confirmation will follow by email.</p>
      </div>
    );
  }
  if (noFree || noPaid) {
    return (
      <div className="text-left border border-border bg-brand-grey/30 p-5 text-sm text-brand-darkgrey" data-testid="appointment-none">
        No appointment times are available to book right now. Our team will be in touch to arrange a suitable time.
      </div>
    );
  }

  // Returning client: paid options + update + urgency + documents
  if (data?.returning) {
    return (
      <div className="text-left border border-border bg-white p-5 space-y-5" data-testid="appointment-returning">
        <div>
          <p className="text-sm font-semibold text-brand-black flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-brand-gold" strokeWidth={1.5} /> Book a paid consultation
          </p>
          <p className="text-xs text-brand-darkgrey mt-1">
            Our records show you have already had your free initial consultation. Please choose a paid consultation below.
          </p>
          <p className="text-xs text-brand-darkgrey mt-1">
            Prices (including VAT): 30 minutes £78 · 60 minutes £138 · 2 hours £250.
          </p>
        </div>

        <div>
          <label className="text-sm font-semibold text-brand-black">Please update us on your situation since we last spoke</label>
          <textarea value={update} onChange={(e) => setUpdate(e.target.value)} rows={4} data-testid="returning-update"
            className="mt-2 w-full border border-border rounded-sm p-3 text-sm focus:outline-none focus:ring-1 focus:ring-brand-gold" />
        </div>

        <label className="flex items-start gap-2 text-sm text-brand-darkgrey cursor-pointer">
          <input type="checkbox" checked={urgent} onChange={(e) => setUrgent(e.target.checked)} data-testid="returning-urgent" className="mt-1" />
          <span className="flex items-center gap-1"><AlertTriangle className="h-4 w-4 text-brand-danger" /> My matter is now urgent (time-critical)</span>
        </label>

        <div>
          <label className="text-sm font-semibold text-brand-black block mb-2">Upload any relevant documents (optional)</label>
          <label className="inline-flex items-center gap-2 text-sm border border-border px-3 py-2 cursor-pointer hover:border-brand-gold rounded-sm" data-testid="returning-upload-label">
            <Upload className="h-4 w-4 text-brand-gold" /> {uploading ? "Uploading…" : "Choose files"}
            <input type="file" multiple onChange={onUpload} className="hidden" data-testid="returning-upload-input"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.heic" />
          </label>
          {files.length > 0 && (
            <ul className="mt-2 text-xs text-brand-darkgrey list-disc pl-5">
              {files.map((f) => <li key={f.id}>{f.filename}</li>)}
            </ul>
          )}
        </div>

        <div className="space-y-5">
          {(data.options || []).map((opt) => (
            (opt.slots || []).length > 0 && (
              <div key={opt.duration} data-testid={`paid-option-${opt.duration}`}>
                <p className="text-sm font-semibold text-brand-black mb-2">
                  {opt.duration}-minute consultation{opt.price ? <span className="text-brand-gold"> — {opt.price} incl. VAT</span> : null}
                </p>
                <SlotGrid slots={opt.slots} selected={selected}
                  onSelect={(s) => setSelected({ ...s, calendar_id: opt.calendar_id })} />
              </div>
            )
          ))}
        </div>

        <Button onClick={confirm} disabled={!selected || booking} data-testid="appointment-confirm-button"
          className="bg-brand-black text-white hover:bg-brand-gold rounded-sm">
          {booking ? "Booking…" : selected ? `Book ${timeLabel(selected.start_time)} on ${dayLabel(selected.start_time)}` : "Select a time"}
        </Button>
      </div>
    );
  }

  // New client: free consultation picker
  return (
    <div className="text-left border border-border bg-white p-5" data-testid="appointment-picker">
      <p className="text-sm font-semibold text-brand-black flex items-center gap-2 mb-1">
        <CalendarClock className="h-4 w-4 text-brand-gold" strokeWidth={1.5} /> Book your free consultation
      </p>
      <p className="text-xs text-brand-darkgrey mb-4">Choose a time that suits you. Booking an appointment is optional.</p>
      <SlotGrid slots={data.slots} selected={selected} onSelect={(s) => setSelected({ ...s, calendar_id: data.calendar_id })} />
      <Button onClick={confirm} disabled={!selected || booking} data-testid="appointment-confirm-button"
        className="mt-5 bg-brand-black text-white hover:bg-brand-gold rounded-sm">
        {booking ? "Booking…" : selected ? `Book ${timeLabel(selected.start_time)} on ${dayLabel(selected.start_time)}` : "Select a time"}
      </Button>
    </div>
  );
};
